import {
  IOutSingleMerchantDTO,
  IOutSingleMerchantCampaignDTO
} from "@app/modules/merchant-single-header/merchant-single-header.interface";
import { FindAllMerchantsByFiltersStatusEnum } from "@app/bf-api";
import { IWPPageMeta } from "@app/api/wp-page-fetcher/module-selector/main-wp.interface";
import { findPlatformContent, getAbsolutePageUrl } from "@app/api/wp-page-fetcher/utils/platform-content-array";
import { getWebSiteId } from "@app/api/wp-page-fetcher/utils/schema-urls";
import { stripHtmlForSchema } from "@app/api/wp-page-fetcher/utils/strip-html-for-schema";
import { JsonLdNode } from "@app/api/wp-page-fetcher/utils/to-json-ld";

/** Taken from the API's own status set, so a new status cannot silently pass as running. */
const ACTIVE_DURATION_STATUSES: string[] = [
  FindAllMerchantsByFiltersStatusEnum.ONGOING,
  FindAllMerchantsByFiltersStatusEnum.ABOUT_TO_EXPIRE
];

/** Schema.org expects ISO 8601, so an unparseable date is dropped rather than passed through raw. */
const toIsoDate = (value?: string): string | undefined => {
  if (!value) return undefined;

  const parsed = Date.parse(value);

  return Number.isNaN(parsed) ? undefined : new Date(parsed).toISOString();
};

/** Fallback for when the API omits `durationStatus`, so a live campaign isn't silently dropped. */
const isWithinCampaignDates = (campaign: IOutSingleMerchantCampaignDTO): boolean => {
  const start = campaign.startDate ? Date.parse(campaign.startDate) : NaN;
  const end = campaign.endDate ? Date.parse(campaign.endDate) : NaN;

  // With no status and no dates there is nothing confirming the campaign runs today, and an
  // unconfirmed campaign must not be presented as the page's current offer.
  if (Number.isNaN(start) && Number.isNaN(end)) return false;

  const now = Date.now();

  if (!Number.isNaN(start) && now < start) return false;
  if (!Number.isNaN(end) && now > end) return false;

  return true;
};

/** An expired, upcoming or offline campaign may not be presented as the page's current offer. */
const isActiveCampaign = (campaign?: IOutSingleMerchantCampaignDTO): boolean => {
  if (!campaign) return false;

  const status = campaign.durationStatus?.name;

  return status ? ACTIVE_DURATION_STATUSES.includes(status) : isWithinCampaignDates(campaign);
};

/**
 * The shop's current campaign as an `Offer`.
 *
 * A general shop campaign ("tot 50% korting") has no fixed product price, so no `price`,
 * `priceCurrency`, `availability` or `itemCondition` is emitted — a missing price must never be
 * rendered as € 0,00. The discount message already lives in `name` and `description`.
 */
const getMainOffer = (merchant: IOutSingleMerchantDTO, platformId: number, url: string) => {
  const { campaign } = merchant;

  if (!isActiveCampaign(campaign)) return undefined;

  // The short campaign text carries the actual offer; without it there is nothing to describe.
  const name = stripHtmlForSchema(campaign?.excerpt);

  if (!name) return undefined;

  return {
    "@type": "Offer",
    "@id": `${url}#main-offer`,
    name,
    url,
    description: stripHtmlForSchema(campaign?.description),
    validFrom: toIsoDate(campaign?.startDate),
    validThrough: toIsoDate(campaign?.endDate),
    offeredBy: {
      "@type": "Organization",
      "@id": `${url}#organization`,
      name: merchant.name,
      url: merchant.url
    },
    eligibleRegion: {
      "@type": "Country",
      name: findPlatformContent(platformId).country
    }
  };
};

/**
 * Nodes for a shop page: a `CollectionPage` describing the shop overview, with the shop's current
 * campaign linked as `mainEntity`. The page composes them into its single `@graph` together with
 * the deal tiles it shows, see `buildPageStructuredData`. Returns no nodes without a merchant.
 */
export const getMerchantStructuredData = (
  merchant: IOutSingleMerchantDTO,
  platformId: number,
  pageUrl: string,
  pageMeta?: IWPPageMeta
): JsonLdNode[] => {
  if (!merchant) return [];

  const platform = findPlatformContent(platformId);
  const url = getAbsolutePageUrl(platformId, pageUrl);
  const mainOffer = getMainOffer(merchant, platformId, url);

  const collectionPage: JsonLdNode = {
    "@type": "CollectionPage",
    "@id": `${url}#webpage`,
    url,
    // Same resolution order as `getPageMeta`, so the markup and the rendered <title> cannot drift.
    name: merchant.seo?.title || pageMeta?.seoTitle || merchant.name,
    description: merchant.seo?.description || pageMeta?.seoMetaDescription,
    inLanguage: platform.locale,
    isPartOf: { "@id": getWebSiteId(platformId) },
    ...(mainOffer ? { mainEntity: { "@id": mainOffer["@id"] } } : {})
  };

  return [collectionPage, ...(mainOffer ? [mainOffer] : [])];
};
